import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { saveMaxIdToStorage, saveTgIdToStorage, getMaxIdFromUrl, getTgIdFromUrl } from '../../context/messengerContext';
import { savePlatformAuth } from '../../utils/platformAuth';
import './InstallPage.css';

const IconMore = ({ className }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <circle cx="4" cy="9" r="1.5" fill="currentColor" />
        <circle cx="9" cy="9" r="1.5" fill="currentColor" />
        <circle cx="14" cy="9" r="1.5" fill="currentColor" />
    </svg>
);

/** Как в Safari / iOS: скруглённый «квадрат» и стрелка вверх (share) */
const IconShare = ({ className }) => (
    <svg
        className={className}
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {/* U-образная рамка со скруглениями (ближе к «квадрату», верх открыт) */}
        <path d="M8 10H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1" />
        {/* Стрелка вверх */}
        <path d="M12 3v11" />
        <path d="M8.5 6.5 12 3l3.5 3.5" />
    </svg>
);

function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

export default function InstallPage() {
    const { t } = useTranslation('basic');
    const navigate = useNavigate();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);
    const ios = isIOS();
    const standalone = isStandalone();

    // Сохраняем ID из URL при открытии страницы установки
    useEffect(() => {
        const maxId = getMaxIdFromUrl();
        const tgId = getTgIdFromUrl();
        const params = new URLSearchParams(window.location.search);
        const handoffPlatform = params.get('platform');
        const handoffInitData = params.get('platform_init_data');
        
        console.log('📱 InstallPage: Detected - tg_id:', tgId, 'max_id:', maxId, 'iOS:', ios);
        
        // Сохраняем через функции из messengerContext
        // Они автоматически используют cookies для iOS и localStorage для других платформ
        if (maxId) {
            saveMaxIdToStorage(maxId);
        }
        
        if (tgId) {
            saveTgIdToStorage(tgId);
        }

        if (handoffPlatform && handoffInitData) {
            savePlatformAuth(handoffPlatform, handoffInitData);
        }
    }, [ios]);

    useEffect(() => {
        // Проверяем, есть ли уже перехваченное событие из index.js
        if (window.__pwaDeferredPrompt) {
            setDeferredPrompt(window.__pwaDeferredPrompt);
        }

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e.detail ?? window.__pwaDeferredPrompt ?? e);
        };

        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('pwa-beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onInstalled);
        window.addEventListener('pwa-appinstalled', onInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('pwa-beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
            window.removeEventListener('pwa-appinstalled', onInstalled);
        };
    }, []);

    const handleInstall = useCallback(() => {
        const prompt = deferredPrompt || window.__pwaDeferredPrompt;
        if (prompt?.prompt) {
            prompt.prompt();
            prompt.userChoice?.then((result) => {
                if (result.outcome === 'accepted') {
                    setInstalled(true);
                }
            });
            window.__pwaDeferredPrompt = null;
            setDeferredPrompt(null);
        }
    }, [deferredPrompt]);

    const canPrompt = Boolean(deferredPrompt || window.__pwaDeferredPrompt);

    const features = [
        { icon: '\u26A1', text: t('install_page_feature_1') },
        { icon: '\uD83D\uDCE6', text: t('install_page_feature_2') },
        { icon: '\uD83D\uDD14', text: t('install_page_feature_3') },
    ];

    return (
        <div className="installPage">
            <div className="installPage_card">
                <img
                    src="/icons/icon-192.png"
                    alt="CDEK"
                    className="installPage_icon"
                />
                <h1 className="installPage_title">
                    {t('install_page_title')}
                </h1>
                <p className="installPage_subtitle">
                    {t('install_page_subtitle')}
                </p>

                <div className="installPage_features">
                    {features.map((f, i) => (
                        <div key={i} className="installPage_feature">
                            <span className="installPage_featureIcon">{f.icon}</span>
                            <span>{f.text}</span>
                        </div>
                    ))}
                </div>

                {installed || standalone ? (
                    <div className="installPage_success">
                        <span className="installPage_successIcon">{'\uD83C\uDF89'}</span>
                        <h2 className="installPage_successTitle">
                            {t('install_page_success')}
                        </h2>
                        <p className="installPage_successSubtitle">
                            {t('install_page_success_subtitle')}
                        </p>
                        <button
                            className="installPage_button"
                            onClick={() => navigate('/menu')}
                        >
                            {t('install_page_go_home')}
                        </button>
                    </div>
                ) : (
                    <>
                        {canPrompt && (
                            <button
                                className="installPage_button"
                                onClick={handleInstall}
                            >
                                {t('install_page_button')}
                            </button>
                        )}

                        {ios && !canPrompt && (
                            <div className="installPage_iosHint">
                                <p className="installPage_iosHintTitle">
                                    {t('install_page_ios_title')}
                                </p>
                                <p className="installPage_iosHintText">
                                    {t('install_page_ios_steps_title')}
                                </p>
                                <ol className="installPage_iosSteps">
                                    <li className="installPage_iosStep">
                                        <div className="installPage_iosStepFlow">
                                            <span>{t('install_page_ios_two_w1a')}</span>
                                            <span className="installPage_chainSep" aria-hidden="true">→</span>
                                            <IconShare className="installPage_shareIcon" />
                                            <span className="installPage_chainSep" aria-hidden="true">→</span>
                                            <span>{t('install_page_ios_two_home')}</span>
                                        </div>
                                    </li>
                                    <li className="installPage_iosStep">
                                        <div className="installPage_iosStepFlow">
                                            <span>{t('install_page_ios_two_w2a')}</span>
                                            <IconMore className="installPage_moreIcon" />
                                            <span className="installPage_chainSep" aria-hidden="true">→</span>
                                            <span>{t('install_page_ios_two_w2b')}</span>
                                            <IconShare className="installPage_shareIcon" />
                                            <span className="installPage_chainSep" aria-hidden="true">→</span>
                                            <span>{t('install_page_ios_two_home')}</span>
                                        </div>
                                    </li>
                                </ol>
                                <p className="installPage_iosHintText">
                                    {t('install_page_ios_hint')}
                                </p>
                            </div>
                        )}

                        {!ios && !canPrompt && (
                            <button
                                className="installPage_button"
                                disabled
                            >
                                {t('install_page_button')}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
